import { uniq } from 'lodash-es';
import type { LightProjectWithRate, LightProjectWithRateAndPermission } from 'types/project';
import type {
  EnrichedUsersDraftProject,
  UserDraftProject,
  UserDraftProjectsTransform,
} from 'types/users';
import { keyBy } from 'utils/typesafe';

export function modifyUserSelectedProjects(
  transform: UserDraftProjectsTransform,
  projects: Array<LightProjectWithRateAndPermission>
): Array<number> {
  if (transform.spec === 'replace') {
    return transform.projects.map(p => p.project_id);
  }
  const addIds = transform.add.map(p => p.project_id);
  const removeIds = transform.remove;
  return uniq(
    projects
      .filter(project => project.has_permission && !removeIds.includes(project.id))
      .map(project => project.id)
      .concat(addIds)
  );
}

export function buildTransformedUserRateList(
  /** This is the list before transformation and enriching */
  projectsForList: Array<LightProjectWithRate>,
  /** Selection/deselection transformation */
  transform: UserDraftProjectsTransform,
  /** Projects that exist in transform are guaranteed to be found here. For rate_type etc */
  projectsForDropdown: Array<LightProjectWithRateAndPermission>
): Array<EnrichedUsersDraftProject> {
  const projectsWithRatesById = keyBy(projectsForDropdown, 'id');
  const enrichAssociation = (association: UserDraftProject): EnrichedUsersDraftProject => {
    const project = projectsWithRatesById[association.project_id];
    const { rate_type: rateType } = project;
    let draftRate: number | null | undefined = project.rate;
    if (rateType === 'user') {
      draftRate = association.hour_rate;
    }

    return {
      projectId: association.project_id,
      draftRate,
      rateType,
      project,
    };
  };
  const modifications = transform.spec === 'replace' ? transform.projects : transform.update;
  const enrichLightProjectWithRate = (project: LightProjectWithRate): EnrichedUsersDraftProject => {
    const { rate_type: rateType } = project;
    let draftRate: number | null | undefined = project.rate;
    if (rateType === 'user') {
      draftRate =
        modifications.find(({ project_id }) => project_id === project.id)?.hour_rate ??
        project.rate;
    }
    return {
      projectId: project.id,
      project,
      draftRate,
      rateType,
    };
  };

  if (transform.spec === 'replace') {
    return transform.projects.map(enrichAssociation);
  }

  return transform.add
    .map(enrichAssociation)
    .concat(
      projectsForList
        .filter(({ id }) => !transform.remove.includes(id))
        .map(enrichLightProjectWithRate)
    );
}
